import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';

import type { UnsavedChangesModalProps } from './UnsavedChangesModal.types';
import { useTranslation } from 'react-i18next';

/**
 * The app's canonical unsaved-changes confirmation. Copy is parameterised because the same decision
 * shows up in more than one shape — leaving a page, and sending a chat prompt the agent would answer
 * from the last saved version — and they must not read identically.
 *
 * On the design system's Dialog rather than a portal of its own: the hosts that raise this are
 * increasingly sheets, and a modal sheet takes pointer events off the body for everything outside
 * its own layer. Sharing Radix's layer stack is what keeps this clickable on top of one.
 */
export function UnsavedChangesModal(props: UnsavedChangesModalProps) {
  const { t } = useTranslation('common');

  const {
    open,
    onCancel,
    onConfirmLeave,
    title = t('components.unsavedChangesModal.unsavedChanges'),
    message = t('components.unsavedChangesModal.looksLikeYouMadeSome'),
    confirmLabel = t('components.unsavedChangesModal.yesIWantToLeave'),
  } = props;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onCancel()}>
      <DialogContent className="max-w-120 gap-0 p-4" data-testid="unsaved-changes-modal">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <DialogBody className="pb-10 pt-4">
          <p className="text-foreground-alt">{message}</p>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('components.unsavedChangesModal.cancel')}
          </Button>
          <Button type="button" onClick={onConfirmLeave}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

UnsavedChangesModal.displayName = 'UnsavedChangesModal';
